import { useEffect, useRef, useState } from "react";
import { KeyRound, Moon, Sun, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { rpc } from "../../lib/rpc";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const ANTI_BIAS_KEY = "platzwart.antiBias";
const NAME_MAX_LENGTH = 80;

// #46 Punkt 2: eigener Anzeigename als Self-Service. Nutzt den vorhandenen
// Pfad POST /admin/users/{id}/name (Backend erlaubt Eigentümer oder Admin).
// Der Auth-Kontext wird aus der Server-Antwort aktualisiert, nicht aus der
// Eingabe -- Dashboard-Gruß und Shell ziehen damit ohne Reload nach.
function AnzeigenameAendern() {
    const { user, updateUser } = useAuth();
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [error, setError] = useState("");
    const [status, setStatus] = useState("");
    const [busy, setBusy] = useState(false);
    const openBtnRef = useRef(null);
    const inputRef = useRef(null);
    const returnFocus = useRef(false);

    // Tastaturpfad: Nach Abbrechen/Speichern/Escape landet der Fokus wieder
    // auf dem Auslöser statt im Nichts (WCAG 2.4.3).
    useEffect(() => {
        if (!open && returnFocus.current) {
            returnFocus.current = false;
            openBtnRef.current?.focus();
        }
    }, [open]);

    const start = () => {
        setName(user?.name || "");
        setError("");
        setStatus("");
        setOpen(true);
    };

    const close = () => {
        returnFocus.current = true;
        setOpen(false);
    };

    const submit = async (event) => {
        event.preventDefault();
        const next = name.trim();
        if (!next) {
            // Fehler benennen und den Fokus ins fehlerhafte Feld legen (3.3.1).
            setError("Bitte einen Anzeigenamen eingeben.");
            inputRef.current?.focus();
            return;
        }
        if (next === user?.name) {
            close();
            return;
        }
        setBusy(true);
        const res = await rpc.post(`/admin/users/${user.id}/name`, { name: next });
        setBusy(false);
        if (!res.ok) {
            setError(res.error || "Speichern fehlgeschlagen. Bitte erneut versuchen.");
            inputRef.current?.focus();
            return;
        }
        updateUser({ name: res.data?.name ?? next });
        setStatus("Anzeigename gespeichert.");
        toast.success("Anzeigename gespeichert.");
        close();
    };

    if (!open) {
        return (
            <>
                <Button
                    ref={openBtnRef}
                    variant="outline"
                    onClick={start}
                    className="pw-tap mt-3 w-full"
                    data-testid="name-change-open"
                >
                    <UserRound className="mr-2 h-4 w-4" /> Anzeigename ändern
                </Button>
                <p
                    role="status"
                    aria-live="polite"
                    className={status ? "mt-2 text-xs text-foreground" : "sr-only"}
                    data-testid="name-change-status"
                >
                    {status}
                </p>
            </>
        );
    }
    return (
        <form
            onSubmit={submit}
            onKeyDown={(e) => {
                if (e.key === "Escape") close();
            }}
            noValidate
            className="mt-3 flex flex-col gap-3"
            data-testid="name-change-form"
        >
            <div>
                <Label htmlFor="name-change-input" className="text-xs">
                    Anzeigename
                </Label>
                <Input
                    ref={inputRef}
                    id="name-change-input"
                    type="text"
                    autoComplete="nickname"
                    autoFocus
                    maxLength={NAME_MAX_LENGTH}
                    value={name}
                    onChange={(e) => {
                        setName(e.target.value);
                        if (error) setError("");
                    }}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? "name-change-error" : undefined}
                    data-testid="name-change-input"
                />
                {error ? (
                    <p
                        id="name-change-error"
                        role="alert"
                        className="mt-1 text-xs text-destructive"
                        data-testid="name-change-error"
                    >
                        {error}
                    </p>
                ) : null}
            </div>
            <div className="flex gap-2">
                <Button
                    type="button"
                    variant="outline"
                    onClick={close}
                    className="pw-tap flex-1"
                    data-testid="name-change-cancel"
                >
                    Abbrechen
                </Button>
                <Button
                    type="submit"
                    disabled={busy}
                    className="pw-tap flex-1"
                    data-testid="name-change-submit"
                >
                    {busy ? "..." : "Speichern"}
                </Button>
            </div>
        </form>
    );
}

function PasswortAendern() {
    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [repeat, setRepeat] = useState("");
    const [busy, setBusy] = useState(false);

    const reset = () => {
        setCurrent("");
        setNext("");
        setRepeat("");
    };

    const submit = async () => {
        if (!current || !next) return toast.error("Bitte alle Felder ausfüllen.");
        if (next.length < 8)
            return toast.error("Neues Passwort muss mindestens 8 Zeichen lang sein.");
        if (next !== repeat)
            return toast.error("Die Wiederholung stimmt nicht überein.");
        setBusy(true);
        try {
            await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            toast.success("Passwort geändert.");
            reset();
            setOpen(false);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    if (!open) {
        return (
            <Button
                variant="outline"
                onClick={() => setOpen(true)}
                className="pw-tap mt-3 w-full"
                data-testid="pw-change-open"
            >
                <KeyRound className="mr-2 h-4 w-4" /> Passwort ändern
            </Button>
        );
    }
    return (
        <div className="mt-3 flex flex-col gap-3" data-testid="pw-change-form">
            <div>
                <Label className="text-xs">Aktuelles Passwort</Label>
                <Input
                    type="password"
                    autoComplete="current-password"
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    data-testid="pw-change-current"
                />
            </div>
            <div>
                <Label className="text-xs">Neues Passwort (mind. 8 Zeichen)</Label>
                <Input
                    type="password"
                    autoComplete="new-password"
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    data-testid="pw-change-new"
                />
            </div>
            <div>
                <Label className="text-xs">Neues Passwort wiederholen</Label>
                <Input
                    type="password"
                    autoComplete="new-password"
                    value={repeat}
                    onChange={(e) => setRepeat(e.target.value)}
                    data-testid="pw-change-repeat"
                />
            </div>
            <div className="flex gap-2">
                <Button
                    variant="outline"
                    onClick={() => {
                        reset();
                        setOpen(false);
                    }}
                    className="pw-tap flex-1"
                    data-testid="pw-change-cancel"
                >
                    Abbrechen
                </Button>
                <Button
                    onClick={submit}
                    disabled={busy}
                    className="pw-tap flex-1"
                    data-testid="pw-change-submit"
                >
                    {busy ? "..." : "Speichern"}
                </Button>
            </div>
        </div>
    );
}

export default function Einstellungen() {
    const { theme, setTheme } = useTheme();
    const { user, signOut } = useAuth();
    const [antiBias, setAntiBias] = useState(() => localStorage.getItem(ANTI_BIAS_KEY) === "true");

    useEffect(() => {
        localStorage.setItem(ANTI_BIAS_KEY, antiBias ? "true" : "false");
    }, [antiBias]);

    const card = (active, icon, label, desc, onClick, testId) => (
        <button
            type="button"
            onClick={onClick}
            className={`pw-tap flex flex-1 flex-col items-start gap-2 rounded-xl border p-4 text-left transition-colors ${active ? "border-primary bg-primary/10" : "border-border bg-card/40"}`}
            data-testid={testId}
        >
            {icon}
            <span className="text-sm font-semibold text-foreground">{label}</span>
            <span className="text-xs text-muted-foreground">{desc}</span>
        </button>
    );

    return (
        <section className="flex flex-col gap-6" data-testid="einstellungen-page">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Floor Mode
                </div>
                <h1 className="text-3xl font-bold tracking-tight text-foreground">
                    Einstellungen
                </h1>
            </div>

            <div>
                <div className="mb-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Erscheinungsbild
                </div>
                <div className="flex gap-3">
                    {card(
                        theme === "dark-industrial",
                        <Moon className="h-5 w-5 text-primary" />,
                        "Dunkel (Industrial)",
                        "Safety Orange auf tief-petrol",
                        () => setTheme("dark-industrial"),
                        "theme-dark-card"
                    )}
                    {card(
                        theme === "light-corporate",
                        <Sun className="h-5 w-5 text-primary" />,
                        "Hell (Corporate)",
                        "heller Hintergrund, gleiches Branding",
                        () => setTheme("light-corporate"),
                        "theme-light-card"
                    )}
                </div>
            </div>

            <div className="rounded-xl border border-border bg-card/60 p-4">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <Label className="text-sm font-semibold text-foreground">
                            Soll-Menge vor Zählung verbergen (Anti-Bias)
                        </Label>
                        <p className="mt-1 text-xs text-muted-foreground">
                            Verhindert, dass Zähler die erwartete Menge vorher sehen.
                        </p>
                    </div>
                    <Switch
                        checked={antiBias}
                        onCheckedChange={setAntiBias}
                        aria-label="Soll-Menge vor Zählung verbergen (Anti-Bias)"
                        data-testid="anti-bias-toggle"
                    />
                </div>
            </div>

            <div className="rounded-xl border border-border bg-card/60 p-4">
                <div className="text-xs text-muted-foreground">Konto</div>
                <div className="mt-1 text-sm font-medium text-foreground" data-testid="konto-name">
                    {user?.name || "—"}
                </div>
                <div className="text-xs text-muted-foreground" data-testid="konto-email">
                    {user?.email}
                </div>
                <AnzeigenameAendern />
                <PasswortAendern />
                <Button
                    variant="outline"
                    onClick={signOut}
                    className="pw-tap mt-3 w-full"
                    data-testid="einstellungen-logout"
                >
                    Abmelden
                </Button>
            </div>
        </section>
    );
}
